import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Scale, ArrowRight, FileText, MessageSquare, ShieldCheck, Briefcase, Gavel, Mic, Phone, ShieldAlert, PawPrint, Globe2, Lightbulb, HeartPulse } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useCountry } from "../context/CountryContext";
import ReadAloudFAB from "../components/ReadAloudFAB";
import CountrySwitcher from "../components/CountrySwitcher";
import ShareButtons from "../components/ShareButtons";
import AvocatEnOrLogo from "../components/AvocatEnOrLogo";
import LanguageSelector from "../components/LanguageSelector";
import { useT } from "../i18n/LanguageContext";
import GothicBanner from "../components/GothicBanner";
import { PoliceCapIcon, ScannerIcon, PhoneTriangleIcon, MailTriangleIcon, ShieldIcon, BureauIcon } from "../components/sos-icons";

// Tuiles principales — icône blanche sur fond coloré (style aligné avec SOS.jsx)
const MAIN_TILES = [
  {
    id: "medical",
    label: "Urgence médicale",
    labelKey: "tile_medical",
    color_label: "cyan",
    color_key: "color_cyan",
    desc: "Allergies + médicaments + QR code d'urgence + scan puffs/psychotropes — l'IA détecte les interactions dangereuses",
    icon: HeartPulse,
    bg: "bg-cyan-800",
    iconColor: "text-white",
    labelColor: "text-white",
    testid: "home-tile-medical",
  },
  {
    id: "police",
    label: "Contrôle police",
    labelKey: "tile_police",
    color_label: "bleu marine",
    color_key: "color_navy",
    desc: "Décrivez ou enregistrez. L'IA cite la loi et prépare un mail au Comité P",
    icon: PoliceCapIcon,
    bg: "bg-blue-900",
    iconColor: "text-white",
    labelColor: "text-white",
    testid: "home-tile-police",
  },
  {
    id: "bagarre",
    label: "Bouclier",
    labelKey: "tile_shield",
    color_label: "orange",
    color_key: "color_orange",
    desc: "Conflit en cours OU mode protection préventif (RDV à risque, audio 24h)",
    icon: ShieldIcon,
    bg: "bg-orange-600",
    iconColor: "text-white",
    labelColor: "text-white",
    testid: "home-tile-bagarre",
  },
  {
    id: "bureau",
    label: "Bureau officiel",
    labelKey: "tile_bureau",
    color_label: "or",
    color_key: "color_gold",
    desc: "Notaire, avocat, assureur, admin — abus détectés + mail à l'ordre",
    icon: BureauIcon,
    bg: "bg-[#B89C65]",
    iconColor: "text-white",
    labelColor: "text-white",
    testid: "home-tile-bureau",
  },
];
const SCAM_TILES = [
  {
    id: "scam_text",
    num: 5,
    label: "Message suspect",
    labelKey: "tile_scam_msg",
    color_label: "violet",
    color_key: "color_purple",
    desc: "SMS, email, facture douteuse — détecte phishing, faux huissier, faux Bpost",
    icon: MailTriangleIcon,
    bg: "bg-purple-700",
    iconColor: "text-white",
    labelColor: "text-white",
    testid: "home-tile-scam-text",
    outline: false,
  },
  {
    id: "scam_voice",
    num: 6,
    label: "Appel suspect",
    labelKey: "tile_scam_call",
    color_label: "blanc bordure rouge",
    color_key: "color_white_red",
    desc: "« Maman, j'ai eu un accident » — détecte voix clonée et arnaque téléphonique",
    icon: PhoneTriangleIcon,
    bg: "bg-white",
    iconColor: "text-red-600",
    labelColor: "text-slate-900",
    testid: "home-tile-scam-voice",
    outline: true,
  },
];

function LangWelcomeBanner() {
  const { t, lang, isRtl } = useT();
  if (lang === "fr") return null;
  return (
    <div className="bg-amber-50 border-b-2 border-amber-600 text-amber-900 px-4 py-3 text-center text-sm" data-testid="lang-welcome-banner" dir={isRtl ? "rtl" : "ltr"}>
      🌍 {t("welcome_in_your_language")}
    </div>
  );
}


export default function Landing() {
  const { user, loading } = useAuth();
  const { country } = useCountry();
  const { t } = useT();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tileStats, setTileStats] = useState({ helped_by_scenario: {}, collective_by_scenario: {} });

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    fetch(base + "/api/stats/public").then((r) => r.ok ? r.json() : null).then((d) => { if (d) setStats(d); }).catch(() => {});
    fetch(base + "/api/stats/tiles").then((r) => r.ok ? r.json() : null).then((d) => { if (d) setTileStats(d); }).catch(() => {});
  }, []);

  // Petit composant : badges de compteur + « Je ne suis pas seul·e » sur une tuile.
  const TileBadges = ({ scenario, dark = true }) => {
    const helped = tileStats.helped_by_scenario?.[scenario] || 0;
    const collective = tileStats.collective_by_scenario?.[scenario] || 0;
    if (helped === 0 && collective === 0) return null;
    const base = dark
      ? "bg-white/15 text-white border-white/20"
      : "bg-slate-900/10 text-slate-900 border-slate-900/20";
    return (
      <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`tile-badges-${scenario}`}>
        {helped > 0 && (
          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${base}`} data-testid={`tile-helped-${scenario}`}>
            ❤️ {helped} {helped > 1 ? "aidé·es" : "aidé·e"}
          </span>
        )}
        {collective > 0 && (
          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${base}`} data-testid={`tile-collective-${scenario}`}>
            👥 {collective} pas seul·e{collective > 1 ? "s" : ""}
          </span>
        )}
      </div>
    );
  };

  const handleTileClick = (tileId) => {
    // Pour Police/Bagarre/Bureau/Harcèlement/Animaux/Étrangers/Médical : démarrer immédiat.
    const autoTiles = ["scan", "police", "bagarre", "bureau", "harassment", "animal_abuse", "sans_papiers", "medical"];
    const auto = autoTiles.includes(tileId) ? "&auto=1" : "";
    const target = `/sos?action=${tileId}${auto}`;
    if (loading) return;
    if (user) {
      navigate(target);
    } else {
      navigate(`/login?next=${encodeURIComponent(target)}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="landing-page">
      {/* NAV */}
      <nav className="border-b border-slate-200 bg-white/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <AvocatEnOrLogo size={36} variant="full" background="black" className="rounded-sm" />
            <span className="font-serif-display text-xl font-semibold tracking-tight hidden md:inline">Un Avocat en Or</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSelector variant="light" compact />
            <CountrySwitcher variant="compact" />
            <Link to="/pro" data-testid="nav-pro-link" className="hidden sm:block">
              <Button variant="ghost" className="rounded-sm text-sm h-9">
                <Briefcase className="h-3.5 w-3.5 mr-1.5" /> Pro
              </Button>
            </Link>
            {user ? (
              <Link to="/sos" data-testid="nav-dashboard">
                <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white text-sm h-9">
                  Mon espace <ArrowRight className="ml-2 h-3.5 w-3.5 rtl:rotate-180" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link" className="hidden sm:block">
                  <Button variant="ghost" className="rounded-sm text-sm h-9">{t("nav_login")}</Button>
                </Link>
                <Link to="/login?mode=register" data-testid="nav-signup-link">
                  <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white text-sm h-9">
                    {t("cta_start")}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Bandeau de bienvenue multilingue — visible si l'utilisateur a changé de langue */}
      <LangWelcomeBanner />

      {/* HERO COMPACT — Logo + tagline */}
      <section className="border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6 text-center">
          {/* BLASON OFFICIEL — phénix + banderole "Un Avocat en Or" en un seul PNG style Grimbergen */}
          <div className="flex justify-center mb-5">
            <img
              src="/logo-seal-large.png"
              alt="Un Avocat en Or — Blason officiel"
              width={300}
              height={300}
              className="rounded-sm shadow-2xl max-w-full"
              style={{ width: "min(320px, 80vw)", height: "auto", display: "block" }}
              data-testid="landing-seal"
            />
          </div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#B89C65] mb-3">
            Droit {country.id === "be" ? "belge" : country.id === "fr" ? "français" : country.id === "lu" ? "luxembourgeois" : "suisse"} · Aide juridique 24h/24 · {country.flag} {country.name}
          </p>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight mb-3">
            Une urgence ? Un document ? Un litige ?
          </h1>
          <p className="text-base text-slate-600 max-w-2xl mx-auto">
            Touchez la touche correspondante. L'IA vous guide en 5 secondes.
          </p>

          {/* Compteur sobre — personnes aidées (humble, transparent même à 0) + partage */}
          {stats && (
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2" data-testid="people-helped-block">
              {stats.people_helped > 0 ? (
                <p
                  className="text-sm text-slate-500"
                  data-testid="people-helped-counter"
                  aria-label={`${stats.people_helped} ${t("people_helped_label") || "personnes aidées par notre service à ce jour"}`}
                >
                  <span className="font-medium text-slate-700">{stats.people_helped.toLocaleString("fr-BE")}</span>{" "}
                  {t("people_helped_label") || "personnes aidées par notre service à ce jour"}
                </p>
              ) : (
                <p
                  className="text-sm text-slate-500 italic"
                  data-testid="people-helped-counter"
                >
                  <span className="inline-block px-2 py-0.5 mr-1.5 text-[10px] uppercase tracking-wider rounded-sm bg-amber-100 text-amber-800 border border-amber-300 not-italic font-medium">
                    Bêta
                  </span>
                  {t("beta_zero_users") || "Bêta — 0 utilisateur actif pour l'instant"}
                </p>
              )}
              <button
                type="button"
                onClick={async () => {
                  const url = (typeof window !== "undefined") ? window.location.origin : "https://unavocatenor.com";
                  const msg = stats.people_helped > 0
                    ? (t("share_counter_msg") || "{n} personnes ont déjà été aidées par Un Avocat en Or — assistance juridique gratuite, 24h/24. Découvrez :")
                        .replace("{n}", stats.people_helped)
                    : "Un Avocat en Or — assistance juridique gratuite, 24h/24, en 12 langues. Aidez-nous à lancer la bêta :";
                  const shareData = { title: "Un Avocat en Or", text: msg, url };
                  try {
                    if (navigator.share) {
                      await navigator.share(shareData);
                    } else if (navigator.clipboard) {
                      await navigator.clipboard.writeText(`${msg} ${url}`);
                      try {
                        const { toast } = await import("sonner");
                        toast.success("Message copié ! Partagez-le où vous voulez.");
                      } catch {
                        alert("Message copié dans le presse-papier.");
                      }
                    }
                  } catch (e) { /* user cancelled */ }
                }}
                className="text-xs px-3 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 transition flex items-center gap-1.5"
                data-testid="share-counter-btn"
                aria-label={t("share_counter") || "Partager ce compteur"}
              >
                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                {t("share_counter") || "Partager"}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* SOS TILES — 4 grosses tuiles 2x2 dominantes + 2 anti-arnaque + lampe Aladin */}
      <section className="bg-slate-50 py-8 border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          {/* 4 tuiles principales — icône blanche sur fond coloré (XL pour visibilité) */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {MAIN_TILES.map((tile, i) => {
              const Icon = tile.icon;
              return (
                <button
                  key={tile.id}
                  onClick={() => handleTileClick(tile.id)}
                  className={`group ${tile.bg} ${tile.labelColor} rounded-sm p-5 sm:p-6 text-left hover:scale-[1.02] hover:shadow-xl active:scale-[0.98] transition-all duration-200 min-h-[190px] sm:min-h-[220px] flex flex-col justify-between`}
                  data-testid={tile.testid}
                  aria-label={`Touche ${i + 1} : ${t(tile.labelKey) || tile.label}`}
                >
                  <div className={`h-16 w-16 sm:h-20 sm:w-20 rounded-sm bg-white/15 flex items-center justify-center ${tile.iconColor}`}>
                    <Icon className="h-10 w-10 sm:h-12 sm:w-12" />
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-80 mb-1">Touche {i + 1} · {t(tile.color_key) || tile.color_label}</p>
                    <h3 className="font-serif-display text-lg sm:text-xl font-medium leading-tight mb-1">{t(tile.labelKey) || tile.label}</h3>
                    <p className="text-xs opacity-80 leading-relaxed line-clamp-2 hidden sm:block">{tile.desc}</p>
                    <TileBadges scenario={tile.id} dark={!tile.outline} />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Hint pour anti-arnaque */}
          <div className="flex items-center justify-center gap-2 mt-5 mb-2">
            <span className="text-amber-600 animate-bounce">▼</span>
            <p className="text-xs uppercase tracking-[0.2em] text-amber-700 font-medium">Anti-arnaque numérique</p>
            <span className="text-amber-600 animate-bounce">▼</span>
          </div>

          {/* 2 tuiles anti-arnaque */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {SCAM_TILES.map((tile) => {
              const Icon = tile.icon;
              return (
                <button
                  key={tile.id}
                  onClick={() => handleTileClick(tile.id)}
                  className={`group ${tile.bg} ${tile.labelColor} rounded-sm p-4 sm:p-5 text-left hover:scale-[1.02] hover:shadow-xl active:scale-[0.98] transition-all duration-200 min-h-[160px] flex flex-col justify-between ${tile.outline ? "border-2 border-red-600" : ""}`}
                  data-testid={tile.testid}
                  aria-label={`Touche ${tile.num} : ${t(tile.labelKey) || tile.label}`}
                >
                  <div className={`h-14 w-14 sm:h-16 sm:w-16 rounded-sm ${tile.outline ? "bg-red-50" : "bg-white/15"} flex items-center justify-center ${tile.iconColor}`}>
                    <Icon className="h-9 w-9 sm:h-10 sm:w-10" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] opacity-80 mb-1">Touche {tile.num} · {t(tile.color_key) || tile.color_label}</p>
                    <h3 className="font-serif-display text-base sm:text-lg font-medium leading-tight mb-1">{t(tile.labelKey) || tile.label}</h3>
                    <p className="text-[10px] sm:text-xs opacity-70 leading-relaxed line-clamp-2">{tile.desc}</p>
                    <TileBadges scenario={tile.id} dark={!tile.outline} />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Tuile Harcèlement & Travail — pleine largeur, public jeunes + adultes */}
          <div className="mt-4 sm:mt-5 flex items-center justify-center gap-2 mb-2">
            <span className="text-rose-600 animate-bounce">▼</span>
            <p className="text-xs uppercase tracking-[0.2em] text-rose-700 font-medium">Travail · École · Apprentissage</p>
            <span className="text-rose-600 animate-bounce">▼</span>
          </div>
          <button
            onClick={() => handleTileClick("harassment")}
            className="group w-full bg-rose-700 text-white rounded-sm p-5 sm:p-6 text-left hover:scale-[1.01] hover:shadow-xl active:scale-[0.99] transition-all duration-200 flex items-center gap-4 min-h-[120px]"
            data-testid="home-tile-harassment"
            aria-label="Touche 8 : Harcèlement & Travail"
          >
            <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-sm bg-white/15 flex items-center justify-center text-white flex-shrink-0">
              <ShieldAlert className="h-10 w-10 sm:h-12 sm:w-12" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-80 mb-1">Touche 8 · {t("color_pink") || "rose"}</p>
              <h3 className="font-serif-display text-lg sm:text-xl font-medium leading-tight mb-1">{t("tile_harassment") || "Harcèlement & Travail"}</h3>
              <p className="text-xs opacity-90 leading-relaxed">Mobbing, abus apprenti, stage illégal, harcèlement scolaire — IA + syndicat + courriers prêts</p>
              <TileBadges scenario="harassment" dark />
            </div>
          </button>

          {/* Tuile 9 — Maltraitance animale */}
          <button
            onClick={() => handleTileClick("animal_abuse")}
            className="group w-full bg-amber-800 text-white rounded-sm p-5 sm:p-6 text-left hover:scale-[1.01] hover:shadow-xl active:scale-[0.99] transition-all duration-200 flex items-center gap-4 min-h-[120px] mt-3 sm:mt-4"
            data-testid="home-tile-animal-abuse"
            aria-label="Touche 9 : Maltraitance animale"
          >
            <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-sm bg-white/15 flex items-center justify-center text-white flex-shrink-0">
              <PawPrint className="h-10 w-10 sm:h-12 sm:w-12" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-80 mb-1">Touche 9 · {t("color_brown") || "marron"}</p>
              <h3 className="font-serif-display text-lg sm:text-xl font-medium leading-tight mb-1">{t("tile_animal") || "Maltraitance animale"}</h3>
              <p className="text-xs opacity-90 leading-relaxed">Animal battu, négligé, abandonné — plainte police + signalement vétérinaire officiel</p>
              <TileBadges scenario="animal_abuse" dark />
            </div>
          </button>

          {/* Tuile 10 — Sans-papiers / Étrangers (multilingue) */}
          <button
            onClick={() => handleTileClick("sans_papiers")}
            className="group w-full bg-blue-800 text-white rounded-sm p-5 sm:p-6 text-left hover:scale-[1.01] hover:shadow-xl active:scale-[0.99] transition-all duration-200 flex items-center gap-4 min-h-[120px] mt-3 sm:mt-4"
            data-testid="home-tile-sans-papiers"
            aria-label="Touche 10 : Sans-papiers / Étrangers"
          >
            <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-sm bg-white/15 flex items-center justify-center text-white flex-shrink-0">
              <Globe2 className="h-10 w-10 sm:h-12 sm:w-12" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-80 mb-1">Touche 10 · {t("color_blue") || "bleu"} 🌍</p>
              <h3 className="font-serif-display text-lg sm:text-xl font-medium leading-tight mb-1">{t("tile_foreigner") || "Sans-papiers / Étrangers"}</h3>
              <p className="text-xs opacity-90 leading-relaxed">Contrôle, démarche, abus de fonctionnaire — IA répond DANS VOTRE LANGUE et protège vos droits humains</p>
              <TileBadges scenario="sans_papiers" dark />
            </div>
          </button>

          {/* Tuile 11 — Suggestion (en bas, plus discrète) */}
          <button
            onClick={() => handleTileClick("suggestion")}
            className="group w-full bg-yellow-700 text-white rounded-sm p-4 sm:p-5 text-left hover:scale-[1.01] hover:shadow-xl active:scale-[0.99] transition-all duration-200 flex items-center gap-3 min-h-[90px] mt-3 sm:mt-4"
            data-testid="home-tile-suggestion"
            aria-label="Touche 11 : Suggestion d'amélioration"
          >
            <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-sm bg-white/15 flex items-center justify-center text-white flex-shrink-0">
              <Lightbulb className="h-7 w-7 sm:h-8 sm:w-8" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] opacity-80 mb-0.5">Touche 11 · {t("color_yellow") || "jaune"} 💬</p>
              <h3 className="font-serif-display text-base sm:text-lg font-medium leading-tight">{t("tile_suggestion") || "Une idée pour améliorer l'app ?"}</h3>
            </div>
          </button>

          {/* LAMPE D'ALADIN — bloc magique centré (style aligné avec SOS.jsx) */}
          <div className="mt-8 -mx-4 sm:mx-0 sm:rounded-sm bg-gradient-to-br from-amber-50 to-orange-50 border-y sm:border border-amber-200 px-4 sm:px-8 py-8 sm:py-10" data-testid="landing-aladin-section">
            <div className="max-w-md mx-auto text-center">
              <p className="text-xs uppercase tracking-[0.3em] text-amber-700 mb-2">Bouton magique · Bêta</p>
              <h2 className="font-serif-display text-2xl sm:text-3xl font-medium text-slate-900 mb-3">
                Vous ne savez pas quelle touche utiliser ?
              </h2>
              <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                Frottez la lampe et expliquez votre situation à voix haute.
                L'IA choisit pour vous la bonne touche, et la lampe vous répond en français.
              </p>
              <button
                onClick={() => handleTileClick("aladin")}
                className="group relative bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 text-white rounded-full h-24 w-24 sm:h-28 sm:w-28 hover:scale-110 active:scale-95 transition-all duration-200 shadow-2xl hover:shadow-amber-500/60 flex items-center justify-center mx-auto"
                data-testid="home-tile-aladin"
                aria-label="Frottez la lampe — triage vocal"
              >
                <span className="text-5xl sm:text-6xl group-hover:animate-pulse">🪔</span>
                <span className="absolute inset-0 rounded-full bg-amber-400/30 animate-ping" />
              </button>
              <p className="text-sm font-medium text-slate-900 mt-4">Touchez la lampe pour parler</p>
              <p className="text-xs text-slate-500 mt-1">La lampe vous répondra avec la voix du génie</p>
            </div>
          </div>

          <p className="text-xs text-slate-500 text-center mt-6 px-2">
            🎙️ Dictée vocale partout · 📷 Scanner ouvre la caméra · ✉️ Mail prêt à envoyer
          </p>

          {/* Compteur impact temps réel */}
          {stats && (stats.scams_blocked_7d > 0 || stats.documents_decoded_30d > 0 || stats.alerts_total > 0) && (
            <div className="mt-8 border-t border-slate-200 pt-6" data-testid="public-stats">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#B89C65] text-center mb-4">L'app en action</p>
              <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
                <div data-testid="stat-scams">
                  <p className="font-serif-display text-2xl sm:text-3xl font-medium text-slate-900">{stats.scams_blocked_7d}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500 leading-tight mt-1">arnaques détectées<br/>cette semaine</p>
                </div>
                <div data-testid="stat-docs">
                  <p className="font-serif-display text-2xl sm:text-3xl font-medium text-slate-900">{stats.documents_decoded_30d}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500 leading-tight mt-1">documents<br/>décodés (30 j)</p>
                </div>
                <div data-testid="stat-total">
                  <p className="font-serif-display text-2xl sm:text-3xl font-medium text-slate-900">{stats.alerts_total}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500 leading-tight mt-1">personnes<br/>accompagnées</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* COMMENT ÇA MARCHE — Explication des 4 touches */}
      <section className="py-16 sm:py-20 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B89C65] mb-3">Mode d'emploi</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-medium tracking-tight">
              Comment fonctionnent les 13 touches d'urgence
            </h2>
          </div>

          {/* Bloc explicatif — les 3 nouvelles fonctions clés */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12" data-testid="new-features-block">
            <div className="bg-rose-50 border-l-4 border-rose-600 p-5 rounded-sm">
              <div className="text-3xl mb-2" aria-hidden="true">❤️</div>
              <h3 className="font-serif-display text-lg font-medium text-rose-900 mb-2">Compteur honnête</h3>
              <p className="text-sm text-rose-800 leading-relaxed">
                « X personnes aidées par notre service » — un chiffre transparent qui ne compte que les vrais utilisateurs. Il monte à chaque personne réelle qui utilise l'app. Partagez-le en un clic pour lancer le bouche-à-oreille.
              </p>
            </div>
            <div className="bg-purple-50 border-l-4 border-purple-600 p-5 rounded-sm">
              <div className="text-3xl mb-2" aria-hidden="true">👥</div>
              <h3 className="font-serif-display text-lg font-medium text-purple-900 mb-2">Vous n'êtes pas seul·e</h3>
              <p className="text-sm text-purple-800 leading-relaxed">
                Sur chaque tuile, un badge indique combien de personnes ont vécu la même situation. Vous pouvez rejoindre une plainte collective — votre témoignage ajouté aux autres fait pression sur les autorités et augmente vos chances de justice.
              </p>
            </div>
            <div className="bg-cyan-50 border-l-4 border-cyan-600 p-5 rounded-sm">
              <div className="text-3xl mb-2" aria-hidden="true">🩺</div>
              <h3 className="font-serif-display text-lg font-medium text-cyan-900 mb-2">Consultation sécurisée</h3>
              <p className="text-sm text-cyan-800 leading-relaxed">
                Dans la tuile Médical, un 4ᵉ onglet écoute votre médecin PENDANT la prescription (avec son accord). Si un composant allergène est prononcé, votre téléphone vibre fort et affiche une alerte rouge : signalez le risque avant que l'ordonnance soit signée. Ça peut sauver une vie.
              </p>
            </div>
          </div>

          <div className="space-y-12">
            <ExplainRow
              icon={ScannerIcon}
              num="1"
              title="Scanner un document"
              points={[
                "Touchez la touche → la caméra s'ouvre automatiquement",
                "Photographiez le courrier (huissier, contravention, jugement, fisc...)",
                "L'IA traduit en français simple : qui envoie, ce qu'on vous demande, les délais",
                "Une lettre de réponse complète est prête à envoyer en 1 clic",
              ]}
              color="bg-slate-900 text-white"
            />
            <ExplainRow
              icon={PoliceCapIcon}
              num="2"
              title="Contrôle de police"
              points={[
                "Décrivez (texte ou voix) ou enregistrez ce que dit l'agent",
                "L'IA détecte les abus potentiels et cite les articles de loi violés",
                "3-5 phrases polies mais fermes à dire à l'agent MAINTENANT",
                `Mail à ${country.police_complaint_body.split("(")[0].trim()} prêt à envoyer si l'agent persiste`,
              ]}
              color="bg-blue-900 text-white"
            />
            <ExplainRow
              icon={ShieldIcon}
              num="3"
              title="Bouclier — Conflit ou protection préventive"
              points={[
                "Mode 'En cours' : conflit physique → vidéo + audio + GPS, plainte prête",
                "Mode 'Préventif' : avant un RDV à risque → audio sauvegardé 24h",
                `Vos protections selon le droit ${country.id === "be" ? "belge" : country.id === "fr" ? "français" : country.id === "lu" ? "luxembourgeois" : "suisse"} (légitime défense, harcèlement)`,
                country.id === "fr"
                  ? "Plainte au procureur de la République prête à déposer"
                  : country.id === "be"
                    ? "Plainte au procureur du Roi prête à déposer"
                    : "Plainte au ministère public prête à déposer",
              ]}
              color="bg-orange-600 text-white"
            />
            <ExplainRow
              icon={BureauIcon}
              num="4"
              title="Bureau (notaire, avocat, assureur, admin)"
              points={[
                "Décrivez ce qu'on essaie de vous imposer",
                "L'IA détecte les clauses abusives, frais cachés, pratiques contestables",
                "Documents que vous devez exiger (devis, mandat écrit, copie dossier...)",
                "Mail à l'ordre déontologique ou au médiateur prêt à envoyer",
              ]}
              color="bg-[#B89C65] text-white"
            />
            <ExplainRow
              icon={MailTriangleIcon}
              num="5"
              title="Message suspect (SMS, email, facture)"
              points={[
                "Collez le texte du message OU prenez-le en photo",
                `L'IA détecte les arnaques ${country.id === "be" ? "belges" : country.id === "fr" ? "françaises" : "locales"} courantes (${
                  country.id === "be" ? "faux Bpost, faux huissier, smishing bancaire" :
                  country.id === "fr" ? "faux Ameli, faux CPF, faux La Poste" :
                  country.id === "lu" ? "faux Post Luxembourg, faux CCSS" :
                  "faux SwissPost, faux AFC, faux PostFinance"
                })`,
                "Verdict immédiat : Légitime / Suspect / Arnaque + score de risque",
                `Conseils anti-arnaque et numéro ${country.antiscam_org} ${country.antiscam_phone} pour signaler`,
              ]}
              color="bg-purple-700 text-white"
            />
            <ExplainRow
              icon={PhoneTriangleIcon}
              num="6"
              title="Appel suspect (voix clonée, faux proche)"
              points={[
                "Enregistrez l'appel en direct OU uploadez une messagerie vocale",
                "L'IA transcrit puis détecte les patterns d'arnaque téléphonique",
                "Questions précises à poser pour démasquer un imposteur",
                "Protection des seniors contre les deepfakes IA et le 'faux fils en détresse'",
              ]}
              color="bg-red-600 text-white"
            />
          </div>
        </div>
      </section>

      {/* AUTRES OPTIONS */}
      <section className="py-16 sm:py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B89C65] mb-3">Au-delà de l'urgence</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-medium tracking-tight">
              Toutes vos questions juridiques, sans rendez-vous
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            <FeatureCard
              icon={MessageSquare}
              title="Chat juridique"
              desc="Posez vos questions en droit du travail, famille, immobilier, fiscal, pénal... Réponses sourcées avec articles du Code belge."
              testid="feature-chat"
            />
            <FeatureCard
              icon={Gavel}
              title="Qui a raison ?"
              desc="Soumettez photo, audio d'une dispute ou un texte. L'IA analyse selon le droit belge — verdict tranché ou analyse nuancée."
              testid="feature-analyses"
            />
            <FeatureCard
              icon={FileText}
              title="Documents officiels"
              desc="Mises en demeure, résiliations, contestations d'amendes. Générées en 30s, conformes au droit belge, exportables en PDF."
              testid="feature-documents"
            />
          </div>
        </div>
      </section>

      {/* CONFIANCE / SECURITÉ */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          <Stat icon={ShieldCheck} label="Confidentiel" value="Chiffré" />
          <Stat icon={Mic} label="Dictée vocale" value="FR" />
          <Stat icon={Phone} label="Urgence" value={`${country.police} / ${country.emergency}`} />
          <Stat icon={Scale} label={`Droit ${country.id === "be" ? "belge" : country.id === "fr" ? "français" : country.id === "lu" ? "lux." : "suisse"}`} value="Codes à jour" />
        </div>
      </section>

      {/* PARTAGE — bouton viral, juridiction-aware */}
      <section className="py-12 sm:py-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <ShareButtons variant="card" data-testid="landing-share" />
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16 sm:py-20 bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-serif-display text-3xl sm:text-4xl font-medium tracking-tight mb-4">
            Installez sur votre téléphone
          </h3>
          <p className="text-slate-300 text-base mb-8 max-w-xl mx-auto">
            En cas d'urgence, 1 tap depuis l'écran d'accueil. Sans inscription pour les essais.
          </p>
          {!user && (
            <Link to="/login?mode=register" data-testid="cta-signup">
              <Button size="lg" className="rounded-sm bg-[#B89C65] hover:bg-[#A38A58] text-white px-10 h-12">
                Créer un compte gratuit <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 border-t border-slate-200 bg-white" data-testid="landing-footer">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Scale className="h-4 w-4 text-slate-900" />
              <span className="font-serif-display text-sm font-semibold">Un Avocat en Or</span>
            </div>
            <p className="text-xs text-slate-500 max-w-md text-center sm:text-right">
              Outil informatif. Ne remplace pas une consultation avec un avocat qualifié.
            </p>
          </div>
          <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-3 text-center sm:text-left leading-relaxed" data-testid="legal-mentions">
            <strong>Éditeur</strong> : Robin Oschmann, entrepreneur individuel — enseigne « Un Avocat en Or »<br />
            <strong>Siège</strong> : 54 rue Fond du Sart, 4171 Comblain-au-Pont, Belgique<br />
            <strong>BCE</strong> : 0838.152.848 — <strong>TVA</strong> : BE 0838.152.848<br />
            Contact : <a href="mailto:contact@unavocatenor.com" className="underline hover:text-slate-800">contact@unavocatenor.com</a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-slate-500 pt-2" data-testid="footer-legal-links">
            <Link to="/privacy" className="underline hover:text-slate-800" data-testid="footer-privacy-link">Politique de confidentialité</Link>
            <span className="text-slate-300">·</span>
            <Link to="/terms" className="underline hover:text-slate-800" data-testid="footer-terms-link">Conditions d'utilisation</Link>
            <span className="text-slate-300">·</span>
            <Link to="/media-kit" className="underline hover:text-slate-800" data-testid="footer-media-kit-link">Kit presse</Link>
          </div>
        </div>
      </footer>
      <ReadAloudFAB targetSelector='[data-testid="landing-page"]' />
    </div>
  );
}

function ExplainRow({ icon: Icon, num, title, points, color }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start" data-testid={`explain-${num}`}>
      <div className="md:col-span-3">
        <div className={`h-16 w-16 rounded-sm flex items-center justify-center ${color}`}>
          <Icon className="h-7 w-7" />
        </div>
        <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mt-3 mb-1">Touche {num}</p>
        <h3 className="font-serif-display text-2xl font-medium">{title}</h3>
      </div>
      <ul className="md:col-span-9 space-y-2">
        {points.map((p, i) => (
          <li key={i} className="flex gap-3 text-sm text-slate-700">
            <span className="text-[#B89C65] flex-shrink-0">—</span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc, testid }) {
  return (
    <div className="bg-white p-7 hover:bg-slate-50 transition-colors" data-testid={testid}>
      <div className="h-10 w-10 flex items-center justify-center border border-slate-200 text-slate-900 mb-5">
        <Icon className="h-5 w-5" />
      </div>
      <h4 className="font-serif-display text-lg font-semibold mb-2">{title}</h4>
      <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex flex-col items-center">
      <Icon className="h-5 w-5 text-[#B89C65] mb-2" />
      <p className="text-base font-medium text-slate-900">{value}</p>
      <p className="text-xs uppercase tracking-widest text-slate-400">{label}</p>
    </div>
  );
}
